/**
 * Loudness — EBU R128 / ITU-R BS.1770-4 integrated loudness measurement
 * and gain-matching utilities. Pure JS, no WASM, no model fetch.
 *
 * The Pitch tool uses this to "match original loudness" — pitch-shifting
 * via the phase-vocoder engine can drift the perceived loudness by ±1-3 dB on big
 * shifts (especially with formant-preserve off). Without normalisation,
 * a user lands a ±5 st transposition and the output is noticeably
 * quieter or louder than the source — even though they didn't ask
 * for a volume change.
 *
 * The standard says the integrated loudness measurement consists of:
 *   1. K-weighted pre-filter (high-shelf + high-pass).
 *   2. 400 ms blocks at 75 % overlap (100 ms hop).
 *   3. Gating: drop blocks below -70 LUFS absolute, then drop blocks
 *      that fall more than 10 LUFS below the running mean of the rest.
 *   4. Average the gated blocks → integrated LUFS (relative to LU = 1).
 *
 * Reference: EBU Tech 3341, BS.1770-4 sections 4-5.
 *
 * Public API:
 *   window.LoudnessR128.measureIntegratedLUFS(audioBuffer) → number
 *      Returns the integrated LUFS for the buffer, or -Infinity for
 *      effectively-silent input.
 *
 *   window.LoudnessR128.applyGainMatch(processedBuffer, targetLUFS, opts?)
 *      Mutates `processedBuffer` in-place by applying a constant gain
 *      so its integrated LUFS lines up with `targetLUFS`. Caps the
 *      true-peak at `opts.truePeakDbtp` (default -1.0 dBTP).
 *      Returns { appliedGainDb, beforeLUFS, afterLUFS, truePeakDbtp }.
 *
 *   window.LoudnessR128.matchTo(processedBuffer, originalBuffer, opts?)
 *      Convenience: measure originalBuffer's LUFS, then gain-match
 *      processedBuffer to it.
 *
 * Performance: O(N) where N is sample count. Pure-JS sample-loop with
 * no allocations inside the inner loop on Float32Arrays. A 5-min
 * stereo file at 48 kHz = 28.8 M samples → ~150 ms on midrange laptops.
 * Acceptable for an export-time post-processing step.
 */
(function(){
'use strict';

// K-filter — the BS.1770-4 two-stage pre-filter (high-shelf + RLB
// high-pass), derived at the requested sample rate via the RBJ Audio-EQ
// Cookbook biquad formulas. At 48 kHz this reproduces the spec Table-1
// coefficients to within rounding; across 8-192 kHz it tracks the
// per-rate analog prototype well inside the ±0.5 LUFS compliance band.
//
// HISTORY — why this was rewritten: the previous derivation built the
// shelf with V = 10^(gain/20). For a SHELF filter the design parameter
// is A = 10^(gain/40) (the shelf reaches A² at the asymptote, so the
// dB-to-linear conversion is /40, not /20). The /20 value over-drove
// the shelf: the K-curve sat ~+2.8 dB high at 1 kHz where it must pass
// at 0 dB, so every integrated-LUFS reading came out ~2-3 LU hot.
// Relative consumers (Pitch's matchTo) were unaffected — both
// measurements shared the bias so the delta cancelled — but anything
// gain-matching to an ABSOLUTE target (audio-restoration) under-shot
// by that amount, and any displayed LUFS number was simply wrong.
// Verified against ffmpeg `ebur128`: corrected filter lands every test
// vector (tone / clipped / pink / silence-gated / 5-min) within
// 0.3 LU of ground truth. See tests/audio/loudness-r128-*.
function makeKFilter(sampleRate){
    // Stage 1 — high-shelf, +4 dB, fc 1681.97 Hz, Q 0.70718.
    // RBJ cookbook high-shelf: DC gain 1.0, HF asymptote A² (= +4 dB).
    var A  = Math.pow(10, 3.999843853973347 / 40);
    var w0 = 2 * Math.PI * 1681.974450955533 / sampleRate;
    var cw = Math.cos(w0), sw = Math.sin(w0);
    var alpha = sw / (2 * 0.7071752369554196);
    var twoSqrtAa = 2 * Math.sqrt(A) * alpha;
    var sb0 =     A * ((A + 1) + (A - 1) * cw + twoSqrtAa);
    var sb1 = -2 * A * ((A - 1) + (A + 1) * cw);
    var sb2 =     A * ((A + 1) + (A - 1) * cw - twoSqrtAa);
    var sa0 =         ((A + 1) - (A - 1) * cw + twoSqrtAa);
    var sa1 =     2 * ((A - 1) - (A + 1) * cw);
    var sa2 =         ((A + 1) - (A - 1) * cw - twoSqrtAa);

    // Stage 2 — RLB high-pass, fc 38.135 Hz, Q 0.50033. RBJ cookbook HP.
    var w2 = 2 * Math.PI * 38.13547087602444 / sampleRate;
    var c2 = Math.cos(w2), s2 = Math.sin(w2);
    var al2 = s2 / (2 * 0.5003270373238773);
    var hb0 =  (1 + c2) / 2, hb1 = -(1 + c2), hb2 = (1 + c2) / 2;
    var ha0 =  1 + al2,      ha1 = -2 * c2,   ha2 = 1 - al2;

    // Return direct-form-I coefficients normalised by a0 (kFilterChannel
    // applies the recurrence with the a-terms subtracted).
    return {
        // Shelf
        sb0: sb0 / sa0, sb1: sb1 / sa0, sb2: sb2 / sa0,
        sa1: sa1 / sa0, sa2: sa2 / sa0,
        // HP
        hb0: hb0 / ha0, hb1: hb1 / ha0, hb2: hb2 / ha0,
        ha1: ha1 / ha0, ha2: ha2 / ha0
    };
}

// Apply the K-filter (shelf → HP) to a single channel, returning a fresh
// Float32Array. Inner loop is straight biquad direct-form-I × 2.
function kFilterChannel(input, c){
    var n = input.length;
    var out = new Float32Array(n);
    var sx1 = 0, sx2 = 0, sy1 = 0, sy2 = 0;
    var hx1 = 0, hx2 = 0, hy1 = 0, hy2 = 0;
    var sb0 = c.sb0, sb1 = c.sb1, sb2 = c.sb2, sa1 = c.sa1, sa2 = c.sa2;
    var hb0 = c.hb0, hb1 = c.hb1, hb2 = c.hb2, ha1 = c.ha1, ha2 = c.ha2;
    for (var i = 0; i < n; i++) {
        var x = input[i];
        // Shelf
        var sy = sb0 * x + sb1 * sx1 + sb2 * sx2 - sa1 * sy1 - sa2 * sy2;
        sx2 = sx1; sx1 = x;
        sy2 = sy1; sy1 = sy;
        // High-pass
        var hy = hb0 * sy + hb1 * hx1 + hb2 * hx2 - ha1 * hy1 - ha2 * hy2;
        hx2 = hx1; hx1 = sy;
        hy2 = hy1; hy1 = hy;
        out[i] = hy;
    }
    return out;
}

/**
 * Fused K-filter + per-100 ms-hop mean-square. ALLOCATES NOTHING.
 *
 * Metering never needs the filtered signal itself — only the sum of its squares
 * per hop — so filtering and accumulating in one pass removes the full-length
 * Float32Array that kFilterChannel returns. That copy was the single biggest
 * allocation in a master: measured 2026-07-30 on a 4-minute stereo track, the
 * `measure` stage grew the heap by 286 MB of a 523 MB total, and on user 3450's
 * 7:51 / 24-bit FLAC the whole render peaked at 1148 MB — unreachable on his
 * 2 GB machine (his "Array buffer allocation failed").
 *
 * `out[h]` accumulates `weight × mean-square(hop h)` across channels, exactly
 * as the callers did before.
 *
 * Arithmetic note: kFilterChannel stores each sample into a Float32Array, so it
 * squares float32-rounded values; this path keeps float64 throughout. The result
 * differs in the last bits only (measured < 1e-5 dB on real music — see
 * loudness-r128-memory.mjs, which asserts it). Use kFilterChannel when you need
 * the filtered signal — the accuracy test against ffmpeg still does.
 */
function kFilterHopPower(data, c, hopLen, nH, weight, out){
    var sx1 = 0, sx2 = 0, sy1 = 0, sy2 = 0;
    var hx1 = 0, hx2 = 0, hy1 = 0, hy2 = 0;
    var sb0 = c.sb0, sb1 = c.sb1, sb2 = c.sb2, sa1 = c.sa1, sa2 = c.sa2;
    var hb0 = c.hb0, hb1 = c.hb1, hb2 = c.hb2, ha1 = c.ha1, ha2 = c.ha2;
    var i = 0;
    for (var h = 0; h < nH; h++) {
        var sm = 0, end = i + hopLen;
        for (; i < end; i++) {
            var x = data[i];
            var sy = sb0 * x + sb1 * sx1 + sb2 * sx2 - sa1 * sy1 - sa2 * sy2;
            sx2 = sx1; sx1 = x;
            sy2 = sy1; sy1 = sy;
            var hy = hb0 * sy + hb1 * hx1 + hb2 * hx2 - ha1 * hy1 - ha2 * hy2;
            hx2 = hx1; hx1 = sy;
            hy2 = hy1; hy1 = hy;
            sm += hy * hy;
        }
        out[h] += weight * (sm / hopLen);
    }
}

// Channel weights from BS.1770-4 Table 4 — front L/R = 1.0,
// centre = 1.0, surround L/R = 1.41 (≈ +1.5 dB). For stereo and mono
// inputs the weight is always 1.0 per channel.
function channelWeight(channelIndex, totalChannels){
    if (totalChannels <= 2) return 1.0;
    // Standard 5.1 layout: [L, R, C, LFE, Ls, Rs]. LFE excluded from
    // loudness measurement per the spec. We approximate by zeroing it.
    if (totalChannels === 6) {
        if (channelIndex === 3) return 0; // LFE
        if (channelIndex >= 4) return 1.41; // surround
        return 1.0;
    }
    return 1.0;
}

function measureIntegratedLUFS(buffer){
    if (!buffer || typeof buffer.getChannelData !== 'function') return -Infinity;
    var sr = buffer.sampleRate;
    var ch = buffer.numberOfChannels;
    var len = buffer.length;
    if (len < sr * 0.4) return -Infinity; // too short for one 400 ms block

    var coefs = makeKFilter(sr);

    // K-filter every channel, accumulate weighted mean-square per block.
    var blockSize = Math.round(sr * 0.4);
    var hopSize   = Math.round(sr * 0.1);
    var numBlocks = Math.floor((len - blockSize) / hopSize) + 1;
    if (numBlocks < 1) return -Infinity;

    var blockMS = new Float64Array(numBlocks);
    var totalWeight = 0;
    var c, weight, b;

    // A 400 ms block is exactly four 100 ms hops at every real sample rate, so
    // accumulate per hop and compose the blocks — no full-length filtered copy
    // (see kFilterHopPower) and no 4× re-summing of the overlap. Guarded: at an
    // exotic rate where round(sr·0.4) ≠ 4·round(sr·0.1) the old path still runs.
    var nH = numBlocks + 3;
    var fused = (blockSize === 4 * hopSize) && (nH * hopSize <= len);

    if (fused) {
        var hopP = new Float64Array(nH);
        for (c = 0; c < ch; c++) {
            weight = channelWeight(c, ch);
            if (weight === 0) continue;
            totalWeight += weight;
            kFilterHopPower(buffer.getChannelData(c), coefs, hopSize, nH, weight, hopP);
        }
        for (b = 0; b < numBlocks; b++) {
            blockMS[b] = (hopP[b] + hopP[b + 1] + hopP[b + 2] + hopP[b + 3]) / 4;
        }
    } else {
        for (c = 0; c < ch; c++) {
            weight = channelWeight(c, ch);
            if (weight === 0) continue;
            totalWeight += weight;
            var filtered = kFilterChannel(buffer.getChannelData(c), coefs);
            for (b = 0; b < numBlocks; b++) {
                var off = b * hopSize;
                var s = 0;
                for (var i = 0; i < blockSize; i++) {
                    var v = filtered[off + i];
                    s += v * v;
                }
                blockMS[b] += weight * (s / blockSize);
            }
        }
    }
    if (totalWeight === 0) return -Infinity;

    // Convert per-block weighted-sum-of-MS to LU. Per BS.1770 the
    // integrated loudness uses this conversion: L = -0.691 + 10·log10(sum).
    // The block-power values are sum-of-channel-weighted-MS.
    var blockLU = new Float64Array(numBlocks);
    for (var b1 = 0; b1 < numBlocks; b1++) {
        var p = blockMS[b1];
        blockLU[b1] = p > 0 ? (-0.691 + 10 * Math.log10(p)) : -Infinity;
    }

    // Absolute gating: drop everything below -70 LUFS.
    var GATE_ABS = -70;
    var keptAbs = [];
    var sumAbsP = 0;
    for (var b2 = 0; b2 < numBlocks; b2++) {
        if (blockLU[b2] >= GATE_ABS) {
            keptAbs.push(b2);
            sumAbsP += blockMS[b2];
        }
    }
    if (keptAbs.length === 0) return -Infinity;

    var meanAbsLU = -0.691 + 10 * Math.log10(sumAbsP / keptAbs.length);

    // Relative gating: drop blocks below (mean - 10 LU).
    var GATE_REL = meanAbsLU - 10;
    var sumRelP = 0;
    var keptRel = 0;
    for (var k = 0; k < keptAbs.length; k++) {
        var bi = keptAbs[k];
        if (blockLU[bi] >= GATE_REL) {
            sumRelP += blockMS[bi];
            keptRel++;
        }
    }
    if (keptRel === 0) return -Infinity;

    var integratedLUFS = -0.691 + 10 * Math.log10(sumRelP / keptRel);
    return integratedLUFS;
}

// True-peak estimate for the gain-stage guard (applyGainMatch), with a +0.5 dB
// safety margin so we back off conservatively rather than ride the cap.
//
// HISTORY — was 4× LINEAR-interpolation, claiming "within 0.3 dB of the proper
// filter". That was false: linear interp `(a+b)/2` is a convex combination,
// mathematically incapable of inter-sample overshoot, so it returned the SAMPLE
// peak. The gain cap was therefore computed on a peak up to ~4 dB too low →
// when the cap bound (boosting quiet/peaky content toward a loud target) the
// normalized output ran HOT: empirically a broadband transient signal capped at
// "-1 dBTP" measured +1.3 dBTP (ffmpeg ebur128) — real inter-sample clipping in
// record / pitch / acx-mastering output. Now delegates to the BS.1770-4 4×
// polyphase FIR (truePeakDbtpDisplay) — the same validated meter — plus margin.
// See reference_true_peak_bs1770_fir.
function truePeakDbtpEstimate(buffer){
    var tp = truePeakDbtpDisplay(buffer);
    return isFinite(tp) ? tp + 0.5 : tp; // +0.5 dB safety margin
}

function applyGainMatch(processedBuffer, targetLUFS, opts){
    opts = opts || {};
    var truePeakCapDbtp = (typeof opts.truePeakDbtp === 'number') ? opts.truePeakDbtp : -1.0;
    var beforeLUFS = measureIntegratedLUFS(processedBuffer);
    if (!isFinite(beforeLUFS) || !isFinite(targetLUFS)) {
        return { appliedGainDb: 0, beforeLUFS: beforeLUFS, afterLUFS: beforeLUFS, truePeakDbtp: -Infinity, skipped: true };
    }
    var deltaDb = targetLUFS - beforeLUFS;

    // Cap the gain so we don't blow past the true-peak limit. If applying
    // `deltaDb` would push the existing peak above the cap, reduce gain
    // accordingly — accepting that we'll under-shoot the loudness target
    // rather than clip the audio.
    var existingPeakDbtp = truePeakDbtpEstimate(processedBuffer);
    if (isFinite(existingPeakDbtp)) {
        var headroom = truePeakCapDbtp - existingPeakDbtp;
        if (deltaDb > headroom) deltaDb = headroom;
    }

    var gain = Math.pow(10, deltaDb / 20);
    var ch = processedBuffer.numberOfChannels, len = processedBuffer.length;
    for (var c = 0; c < ch; c++) {
        var data = processedBuffer.getChannelData(c);
        for (var i = 0; i < len; i++) {
            var v = data[i] * gain;
            // Hard-clamp ±1 as final safety. If existingPeakDbtp + deltaDb
            // landed at exactly -1 dBTP this never fires; it's only here
            // for the rare numerical spike from inter-sample peaks above
            // our linear-interp estimate.
            if (v > 1) v = 1; else if (v < -1) v = -1;
            data[i] = v;
        }
    }

    var afterLUFS = measureIntegratedLUFS(processedBuffer);
    var afterPeak = truePeakDbtpEstimate(processedBuffer);
    return {
        appliedGainDb: deltaDb,
        beforeLUFS: beforeLUFS,
        afterLUFS: afterLUFS,
        truePeakDbtp: afterPeak
    };
}

function matchTo(processedBuffer, originalBuffer, opts){
    var targetLUFS = measureIntegratedLUFS(originalBuffer);
    var result = applyGainMatch(processedBuffer, targetLUFS, opts);
    result.targetLUFS = targetLUFS;
    return result;
}

// ITU-R BS.1770-4 Annex 2: 4-phase, 12-tap polyphase FIR for 4× oversampling —
// the same interpolation filter libebur128 / ffmpeg `ebur128=peak=true` use for
// the true-peak meter. Linear interpolation (the old implementation here) is
// mathematically incapable of inter-sample overshoot — `s0+(s1-s0)·t` is a
// convex combination, always between the two samples — so it silently returned
// the *sample* peak and read 5–7 dB low on real material (a clipped master
// reads 0 dBFS sample / +6.6 dBTP true). Validated against ffmpeg on real
// fixtures: max error 0.83 dB (white noise), <0.3 dB on music. See
// reference_true_peak_bs1770_fir.
var TP_FIR = [
    [ 0.0017089843750, 0.0109863281250,-0.0196533203125, 0.0332031250000,-0.0594482421875, 0.1373291015625, 0.9721679687500,-0.1022949218750, 0.0476074218750,-0.0266113281250, 0.0148925781250,-0.0083007812500],
    [-0.0291748046875, 0.0292968750000,-0.0517578125000, 0.0891113281250,-0.1665039062500, 0.4650878906250, 0.7797851562500,-0.2003173828125, 0.1015625000000,-0.0582275390625, 0.0330810546875,-0.0189208984375],
    [-0.0189208984375, 0.0330810546875,-0.0582275390625, 0.1015625000000,-0.2003173828125, 0.7797851562500, 0.4650878906250,-0.1665039062500, 0.0891113281250,-0.0517578125000, 0.0292968750000,-0.0291748046875],
    [-0.0083007812500, 0.0148925781250,-0.0266113281250, 0.0476074218750,-0.1022949218750, 0.9721679687500, 0.1373291015625,-0.0594482421875, 0.0332031250000,-0.0196533203125, 0.0109863281250, 0.0017089843750]
];

// Honest true-peak (BS.1770-4 4× polyphase oversample), NO safety margin — for
// display. `truePeakDbtpEstimate` above keeps a +0.5 dB margin because it guards
// a gain stage; a meter must show the real number. Inter-sample peaks are a
// local function of nearby samples, so the FIR is evaluated only in windows
// around samples within 6 dB of the sample peak — proven Δ=0 vs the full pass on
// every fixture incl. full-scale sine/noise, while skipping the quiet majority.
function truePeakDbtpDisplay(buffer){
    var ch = buffer.numberOfChannels, len = buffer.length;
    if (len < 1) return -Infinity;
    var sp = 0, c, i, d;
    for (c = 0; c < ch; c++) { d = buffer.getChannelData(c); for (i = 0; i < len; i++) { var a = d[i] < 0 ? -d[i] : d[i]; if (a > sp) sp = a; } }
    if (sp <= 0) return -Infinity;
    var thr = sp * 0.5, peak = sp;
    for (c = 0; c < ch; c++) {
        d = buffer.getChannelData(c);
        for (i = 0; i < len; i++) {
            var av = d[i] < 0 ? -d[i] : d[i];
            if (av < thr) continue;
            for (var ph = 0; ph < 4; ph++) {
                var cf = TP_FIR[ph], acc = 0;
                for (var t = 0; t < 12; t++) { var idx = i - t + 6; if (idx >= 0 && idx < len) acc += cf[t] * d[idx]; }
                var aa = acc < 0 ? -acc : acc;
                if (aa > peak) peak = aa;
            }
        }
    }
    return 20 * Math.log10(peak);
}

// Full BS.1770-4 / EBU R128 loudness report in one call: integrated LUFS
// (gated), momentary (400 ms) and short-term (3 s) maxima, loudness range
// (LRA, EBU Tech 3342 — gated short-term P95−P10), plus sample peak, RMS
// and true peak. Tools that show a loudness panel call this instead of
// hand-rolling their own K-weighting — there must be exactly one
// implementation of the standard on the platform.
function measureDetailed(buffer){
    var out = {
        integratedLUFS: -Infinity, momentaryMaxLUFS: -Infinity,
        shortTermMaxLUFS: -Infinity, loudnessRangeLU: 0,
        peakDb: -Infinity, rmsDb: -Infinity, truePeakDb: -Infinity
    };
    if (!buffer || typeof buffer.getChannelData !== 'function') return out;
    var sr = buffer.sampleRate, ch = buffer.numberOfChannels, len = buffer.length;
    if (len < 1) return out;

    // Sample peak + RMS on the unfiltered signal.
    var peak = 0, sumSq = 0, total = 0;
    for (var c = 0; c < ch; c++) {
        var d = buffer.getChannelData(c);
        for (var i = 0; i < len; i++) { var v = d[i], a = v < 0 ? -v : v; if (a > peak) peak = a; sumSq += v * v; }
        total += len;
    }
    if (peak > 0) out.peakDb = 20 * Math.log10(peak);
    if (total > 0 && sumSq > 0) out.rmsDb = 20 * Math.log10(Math.sqrt(sumSq / total));
    out.truePeakDb = truePeakDbtpDisplay(buffer);

    if (len < sr * 0.4) return out; // too short for even one 400 ms block

    // Compute mean-square per 100 ms hop ONCE per channel (O(N)); the
    // 400 ms momentary block is then the mean of 4 hops and the 3 s
    // short-term block the mean of 30 — exact, since 400 ms = 4×100 ms
    // and 3 s = 30×100 ms. The naive nested re-sum was O(N × overlap)
    // — 30× redundant on short-term — and turned a 5-min file into a
    // ~1 s main-thread stall.
    var coefs = makeKFilter(sr);
    var hopLen = Math.round(sr * 0.1);
    var nH = Math.floor(len / hopLen);
    if (nH < 4) return out;
    var hopP = new Float64Array(nH); // weighted mean-square per hop

    var totalWeight = 0;
    for (var c2 = 0; c2 < ch; c2++) {
        var w = channelWeight(c2, ch);
        if (w === 0) continue;
        totalWeight += w;
        // Fused: no full-length filtered copy (see kFilterHopPower).
        kFilterHopPower(buffer.getChannelData(c2), coefs, hopLen, nH, w, hopP);
    }
    if (totalWeight === 0) return out;

    var nM = nH - 3;                       // 400 ms blocks at 100 ms hop
    var nS = nH >= 30 ? nH - 29 : 0;       // 3 s blocks at 100 ms hop
    var mMS = new Float64Array(nM);
    for (var b = 0; b < nM; b++) {
        mMS[b] = (hopP[b] + hopP[b + 1] + hopP[b + 2] + hopP[b + 3]) / 4;
    }
    var sMS = nS > 0 ? new Float64Array(nS) : null;
    if (nS > 0) {
        var run = 0, hi;
        for (hi = 0; hi < 30; hi++) run += hopP[hi];
        sMS[0] = run / 30;
        for (var bs = 1; bs < nS; bs++) {
            run += hopP[bs + 29] - hopP[bs - 1];
            sMS[bs] = run / 30;
        }
    }

    function toLU(p){ return p > 0 ? (-0.691 + 10 * Math.log10(p)) : -Infinity; }

    // Momentary max + integrated (absolute -70 LUFS then relative -10 LU gate).
    var keptAbs = [], sumAbs = 0, momMax = -Infinity;
    for (var b1 = 0; b1 < nM; b1++) {
        var lu = toLU(mMS[b1]);
        if (lu > momMax) momMax = lu;
        if (lu >= -70) { keptAbs.push(b1); sumAbs += mMS[b1]; }
    }
    out.momentaryMaxLUFS = momMax;
    if (keptAbs.length) {
        var relGate = (-0.691 + 10 * Math.log10(sumAbs / keptAbs.length)) - 10;
        var sumRel = 0, nRel = 0;
        for (var ki = 0; ki < keptAbs.length; ki++) {
            var idx = keptAbs[ki];
            if (toLU(mMS[idx]) >= relGate) { sumRel += mMS[idx]; nRel++; }
        }
        if (nRel) out.integratedLUFS = -0.691 + 10 * Math.log10(sumRel / nRel);
    }

    // Short-term max + LRA (EBU Tech 3342: short-term values, absolute
    // -70 LUFS gate then relative -20 LU gate, then P95 − P10 spread).
    if (nS > 0) {
        var stLU = [], stMax = -Infinity;
        for (var b2 = 0; b2 < nS; b2++) {
            var slu = toLU(sMS[b2]);
            if (slu > stMax) stMax = slu;
            stLU.push(slu);
        }
        out.shortTermMaxLUFS = stMax;
        var absKept = [], sumAbsS = 0;
        for (var s1 = 0; s1 < stLU.length; s1++) {
            if (stLU[s1] >= -70) { absKept.push(stLU[s1]); sumAbsS += sMS[s1]; }
        }
        if (absKept.length) {
            var relGateS = (-0.691 + 10 * Math.log10(sumAbsS / absKept.length)) - 20;
            var gated = absKept.filter(function(v){ return v >= relGateS; });
            if (gated.length > 1) {
                gated.sort(function(a, b){ return a - b; });
                var p10 = gated[Math.min(gated.length - 1, Math.floor(gated.length * 0.10))];
                var p95 = gated[Math.min(gated.length - 1, Math.floor(gated.length * 0.95))];
                out.loudnessRangeLU = Math.max(0, p95 - p10);
            }
        }
    }
    return out;
}

// ── Real-time loudness meter ──────────────────────────────────────────
// A streaming BS.1770-4 meter for live recording / monitoring UIs. The
// offline measureDetailed() above re-K-filters the whole buffer every
// call — useless for a 60 fps live readout. createMeter() carries the
// K-filter biquad state across push() calls and keeps a rolling history
// of 100 ms-hop K-weighted power, so momentary (400 ms = last 4 hops)
// and short-term (3 s = last 30 hops) LUFS are O(1) to read.
//
// Crucially this is the SAME K-filter (makeKFilter) and the SAME
// -0.691 + 10·log10 conversion as the offline path — so the number a
// user watches on the live meter agrees with the number measureDetailed
// reports on the finished file. Before this, live LUFS was a hand-rolled
// `rms + 2` guess that disagreed with the offline reading by 2-4 LU.
//
//   var m = LoudnessR128.createMeter(48000, 2);
//   // in the audio callback, per render quantum:
//   m.push([leftFloat32, rightFloat32], frameCount);
//   m.momentaryLUFS();   // 400 ms window, -Infinity until 400 ms seen
//   m.shortTermLUFS();   // 3 s window,  -Infinity until 3 s seen
//   m.integratedLUFS();  // gated, whole-session-so-far
//   m.reset();           // clear on a new take
function createMeter(sampleRate, numChannels){
    var sr = sampleRate || 48000;
    var ch = Math.max(1, numChannels || 1);
    var coefs = makeKFilter(sr);
    var hopLen = Math.round(sr * 0.1); // 100 ms

    // Per-channel biquad state — persists across push() calls so the
    // recurrence is continuous (a fresh state every call would ring on
    // each boundary and bias the reading).
    var st = [];
    for (var c = 0; c < ch; c++) {
        st.push({ sx1:0, sx2:0, sy1:0, sy2:0, hx1:0, hx2:0, hy1:0, hy2:0, w: channelWeight(c, ch) });
    }

    var hopAccum = 0;        // weighted sum-of-squares in the partial hop
    var hopFilled = 0;       // samples accumulated toward the current hop
    var hops = [];           // weighted mean-square per finished 100 ms hop
    var MAX_HOPS = 30;       // 3 s of history is all short-term needs

    // Integrated state — accumulated over the whole session. We keep the
    // running absolute-gated mean cheaply; the relative gate is applied
    // at read time over the retained-power sum. Storing every hop's power
    // forever would be unbounded, so integrated keeps a sum + count of
    // hops that passed the -70 LUFS absolute gate (good enough — the
    // relative gate moves <0.5 LU vs. the full two-pass on speech).
    var intgSumP = 0, intgCount = 0;

    function pushHop(ms){
        hops.push(ms);
        if (hops.length > MAX_HOPS) hops.shift();
        var lu = ms > 0 ? (-0.691 + 10 * Math.log10(ms)) : -Infinity;
        if (lu >= -70) { intgSumP += ms; intgCount++; }
    }

    function meanLU(n){
        if (hops.length < n) return -Infinity;
        var s = 0;
        for (var i = hops.length - n; i < hops.length; i++) s += hops[i];
        var p = s / n;
        return p > 0 ? (-0.691 + 10 * Math.log10(p)) : -Infinity;
    }

    return {
        // channels: array of Float32Array (one per channel), each at least
        // `frames` long. frames: how many samples to consume.
        push: function(channels, frames){
            var n = frames || (channels[0] ? channels[0].length : 0);
            for (var i = 0; i < n; i++) {
                var weighted = 0;
                for (var c = 0; c < ch; c++) {
                    var s = st[c];
                    if (s.w === 0) continue;
                    var data = channels[c] || channels[0];
                    var x = data[i];
                    // Stage 1 — high-shelf
                    var sy = coefs.sb0*x + coefs.sb1*s.sx1 + coefs.sb2*s.sx2 - coefs.sa1*s.sy1 - coefs.sa2*s.sy2;
                    s.sx2 = s.sx1; s.sx1 = x; s.sy2 = s.sy1; s.sy1 = sy;
                    // Stage 2 — RLB high-pass
                    var hy = coefs.hb0*sy + coefs.hb1*s.hx1 + coefs.hb2*s.hx2 - coefs.ha1*s.hy1 - coefs.ha2*s.hy2;
                    s.hx2 = s.hx1; s.hx1 = sy; s.hy2 = s.hy1; s.hy1 = hy;
                    weighted += s.w * hy * hy;
                }
                hopAccum += weighted;
                if (++hopFilled >= hopLen) {
                    pushHop(hopAccum / hopLen);
                    hopAccum = 0; hopFilled = 0;
                }
            }
        },
        momentaryLUFS: function(){ return meanLU(4); },   // 400 ms
        shortTermLUFS: function(){ return meanLU(30); },   // 3 s
        integratedLUFS: function(){
            if (intgCount === 0) return -Infinity;
            return -0.691 + 10 * Math.log10(intgSumP / intgCount);
        },
        reset: function(){
            for (var c = 0; c < ch; c++) {
                var s = st[c];
                s.sx1=s.sx2=s.sy1=s.sy2=s.hx1=s.hx2=s.hy1=s.hy2=0;
            }
            hopAccum = 0; hopFilled = 0; hops.length = 0;
            intgSumP = 0; intgCount = 0;
        }
    };
}

// ── Exact streaming meter ─────────────────────────────────────────────
// createMeter() above is for LIVE readouts: bounded memory, but its
// integrated value applies only the absolute gate (documented ~0.5 LU
// off). The mastering streaming lane needs the number measureDetailed()
// would print — the whole point of two exact passes is that an excerpt
// lies by up to 2 LU on real material (measured 2026-07-31, 11 tracks).
//
// This meter is push()-fed like createMeter but keeps EVERY 100 ms hop
// power (8 bytes per 100 ms — a 2-hour file is ~0.6 MB), so finish()
// can run the full BS.1770-4 two-gate integration, the EBU Tech 3342
// LRA, and the momentary/short-term maxima exactly as the offline path
// does. True peak runs the same BS.1770-4 4× FIR as truePeakDbtpDisplay,
// streamed with an 11-sample carry per channel; its 6 dB evaluation
// window uses the RUNNING sample peak, which can only be lower than the
// final one — a superset of the offline path's evaluations, so the
// result is identical, never approximate.
//
//   var m = LoudnessR128.createIntegratedMeter(sr, nCh);
//   m.push([L, R], frames);        // any chunk sizes, any alignment
//   var report = m.finish();       // same shape as measureDetailed()
//
// opts.truePeak === false skips the 4× FIR entirely — for callers that only
// need the integrated number (the loudness search loop), true peak is ~90%
// of the meter's cost on dense material and pure waste.
function createIntegratedMeter(sampleRate, numChannels, opts){
    var sr = sampleRate || 48000;
    var ch = Math.max(1, numChannels || 1);
    var wantTP = !(opts && opts.truePeak === false);
    var coefs = makeKFilter(sr);
    var hopLen = Math.round(sr * 0.1);

    var st = [], totalWeight = 0;
    for (var c0 = 0; c0 < ch; c0++) {
        var w0 = channelWeight(c0, ch);
        st.push({ sx1:0,sx2:0,sy1:0,sy2:0,hx1:0,hx2:0,hy1:0,hy2:0, w: w0 });
        totalWeight += w0;
    }

    var hopAccum = 0, hopFilled = 0;
    var hopP = new Float64Array(4096), nH = 0;
    function pushHop(v){
        if (nH === hopP.length) {
            var g = new Float64Array(hopP.length * 2);
            g.set(hopP); hopP = g;
        }
        hopP[nH++] = v;
    }

    var samplePeak = 0, sumSq = 0, totalSamples = 0;

    // True-peak carry: last 11 samples per channel, zero-primed so the very
    // first samples get the same zero left-context the offline meter's
    // index clamp gives them. The 12-tap window around p spans [p−5, p+6].
    var tpPeak = 0;
    var tpCarry = [];
    for (var c1 = 0; c1 < ch; c1++) tpCarry.push(new Float32Array(11));

    function tpChannel(data, carry){
        var n = data.length;
        if (!n) return;
        var ext = new Float32Array(11 + n);
        ext.set(carry, 0); ext.set(data, 11);
        var thr = samplePeak * 0.5;
        var last = ext.length - 7;
        for (var p = 5; p <= last; p++) {
            var av = ext[p] < 0 ? -ext[p] : ext[p];
            if (av < thr) continue;
            for (var ph = 0; ph < 4; ph++) {
                var cf = TP_FIR[ph], acc = 0;
                for (var t = 0; t < 12; t++) acc += cf[t] * ext[p - t + 6];
                var aa = acc < 0 ? -acc : acc;
                if (aa > tpPeak) tpPeak = aa;
            }
        }
        carry.set(ext.subarray(ext.length - 11));
    }

    var finished = false;

    return {
        push: function(channels, frames){
            var n = frames || (channels[0] ? channels[0].length : 0);
            if (!n || finished) return;
            var c, i;
            // Raw sample peak first: the true-peak threshold below must
            // already include this chunk, or a loud sample could be judged
            // against a stale (lower-is-fine, but why) threshold.
            for (c = 0; c < ch; c++) {
                var d0 = channels[c] || channels[0];
                for (i = 0; i < n; i++) {
                    var v0 = d0[i], a0 = v0 < 0 ? -v0 : v0;
                    if (a0 > samplePeak) samplePeak = a0;
                    sumSq += v0 * v0;
                }
            }
            totalSamples += n * ch;
            if (wantTP) for (c = 0; c < ch; c++) tpChannel(channels[c] || channels[0], tpCarry[c]);
            // K-filter + hop accumulation, one interleaved pass (state per channel).
            for (i = 0; i < n; i++) {
                var weighted = 0;
                for (c = 0; c < ch; c++) {
                    var s = st[c];
                    if (s.w === 0) continue;
                    var x = (channels[c] || channels[0])[i];
                    var sy = coefs.sb0*x + coefs.sb1*s.sx1 + coefs.sb2*s.sx2 - coefs.sa1*s.sy1 - coefs.sa2*s.sy2;
                    s.sx2 = s.sx1; s.sx1 = x; s.sy2 = s.sy1; s.sy1 = sy;
                    var hy = coefs.hb0*sy + coefs.hb1*s.hx1 + coefs.hb2*s.hx2 - coefs.ha1*s.hy1 - coefs.ha2*s.hy2;
                    s.hx2 = s.hx1; s.hx1 = sy; s.hy2 = s.hy1; s.hy1 = hy;
                    weighted += s.w * hy * hy;
                }
                hopAccum += weighted;
                if (++hopFilled >= hopLen) {
                    pushHop(hopAccum / hopLen);
                    hopAccum = 0; hopFilled = 0;
                }
            }
        },
        // Same report shape as measureDetailed(). Call once.
        finish: function(){
            finished = true;
            var out = {
                integratedLUFS: -Infinity, momentaryMaxLUFS: -Infinity,
                shortTermMaxLUFS: -Infinity, loudnessRangeLU: 0,
                peakDb: -Infinity, rmsDb: -Infinity, truePeakDb: -Infinity
            };
            if (samplePeak > 0) out.peakDb = 20 * Math.log10(samplePeak);
            if (totalSamples > 0 && sumSq > 0) out.rmsDb = 20 * Math.log10(Math.sqrt(sumSq / totalSamples));
            // Flush the true-peak tail: six zero samples stand in for the
            // out-of-range taps the offline meter skips at the buffer edge.
            if (wantTP) for (var c = 0; c < ch; c++) tpChannel(new Float32Array(6), tpCarry[c]);
            var tp = tpPeak > samplePeak ? tpPeak : samplePeak;
            if (tp > 0) out.truePeakDb = 20 * Math.log10(tp);

            if (nH < 4 || totalWeight === 0) return out;

            function toLU(p){ return p > 0 ? (-0.691 + 10 * Math.log10(p)) : -Infinity; }
            var nM = nH - 3;
            var nS = nH >= 30 ? nH - 29 : 0;
            var mMS = new Float64Array(nM);
            for (var b = 0; b < nM; b++) {
                mMS[b] = (hopP[b] + hopP[b + 1] + hopP[b + 2] + hopP[b + 3]) / 4;
            }
            var keptAbs = [], sumAbs = 0, momMax = -Infinity;
            for (var b1 = 0; b1 < nM; b1++) {
                var lu = toLU(mMS[b1]);
                if (lu > momMax) momMax = lu;
                if (lu >= -70) { keptAbs.push(b1); sumAbs += mMS[b1]; }
            }
            out.momentaryMaxLUFS = momMax;
            if (keptAbs.length) {
                var relGate = (-0.691 + 10 * Math.log10(sumAbs / keptAbs.length)) - 10;
                var sumRel = 0, nRel = 0;
                for (var ki = 0; ki < keptAbs.length; ki++) {
                    var idx = keptAbs[ki];
                    if (toLU(mMS[idx]) >= relGate) { sumRel += mMS[idx]; nRel++; }
                }
                if (nRel) out.integratedLUFS = -0.691 + 10 * Math.log10(sumRel / nRel);
            }
            if (nS > 0) {
                var sMS = new Float64Array(nS);
                var run = 0, hi;
                for (hi = 0; hi < 30; hi++) run += hopP[hi];
                sMS[0] = run / 30;
                for (var bs = 1; bs < nS; bs++) {
                    run += hopP[bs + 29] - hopP[bs - 1];
                    sMS[bs] = run / 30;
                }
                var stLU = [], stMax = -Infinity;
                for (var b2 = 0; b2 < nS; b2++) {
                    var slu = toLU(sMS[b2]);
                    if (slu > stMax) stMax = slu;
                    stLU.push(slu);
                }
                out.shortTermMaxLUFS = stMax;
                var absKept = [], sumAbsS = 0;
                for (var s1 = 0; s1 < stLU.length; s1++) {
                    if (stLU[s1] >= -70) { absKept.push(stLU[s1]); sumAbsS += sMS[s1]; }
                }
                if (absKept.length) {
                    var relGateS = (-0.691 + 10 * Math.log10(sumAbsS / absKept.length)) - 20;
                    var gated = absKept.filter(function(v){ return v >= relGateS; });
                    if (gated.length > 1) {
                        gated.sort(function(a, b){ return a - b; });
                        var p10 = gated[Math.min(gated.length - 1, Math.floor(gated.length * 0.10))];
                        var p95 = gated[Math.min(gated.length - 1, Math.floor(gated.length * 0.95))];
                        out.loudnessRangeLU = Math.max(0, p95 - p10);
                    }
                }
            }
            return out;
        }
    };
}

// ── Нарезанные (async) измерители ────────────────────────────────────
// measureDetailed(out) на мастерённом (плотном) материале — это один
// сплошной синхронный блок: true-peak FIR срабатывает почти на каждом
// сэмпле, и 2-минутный трек на слабой машине держит главный поток 4–7 с
// (замер 08.08 при CPU 4×: худший вызов 7 058 мс, «еле текст ввёл» у
// Pro-3450). Промисов внутри мало поставить — .then это микрозадача
// (см. folder-zip, тот же класс); граница задач должна быть настоящей.
// Здесь та же математика течёт через createIntegratedMeter — документально
// тождественный, не приближение — кусками под бюджет времени, с
// макрозадачей между кусками (паттерн buildEnvelopeAsync из лимитера).
function _yieldMacrotask(){
    if (typeof scheduler !== 'undefined' && scheduler.yield) return scheduler.yield();
    return new Promise(function(r){ setTimeout(r, 0); });
}
async function feedMeterSliced(buffer, meter, onProgress){
    var len = buffer.length, ch = buffer.numberOfChannels;
    var chans = [];
    for (var c = 0; c < ch; c++) chans.push(buffer.getChannelData(c));
    // Time-sliced: fixed sample steps swing ~10× in cost between quiet and
    // dense passages (the TP threshold skip), so the budget is milliseconds.
    var SLICE_MS = 24, STEP = 32768, pos = 0;
    var sub = new Array(ch);
    while (pos < len) {
        var t0 = performance.now();
        do {
            var end = pos + STEP; if (end > len) end = len;
            for (var c2 = 0; c2 < ch; c2++) sub[c2] = chans[c2].subarray(pos, end);
            meter.push(sub, end - pos);
            pos = end;
        } while (pos < len && performance.now() - t0 < SLICE_MS);
        if (onProgress) { try { onProgress(pos / len); } catch (e) {} }
        await _yieldMacrotask();
    }
}
// Same report as measureDetailed(), without the multi-second main-thread block.
async function measureDetailedAsync(buffer, opts){
    if (!buffer || typeof buffer.getChannelData !== 'function' || buffer.length < 1) return measureDetailed(buffer);
    var m = createIntegratedMeter(buffer.sampleRate, buffer.numberOfChannels);
    await feedMeterSliced(buffer, m, opts && opts.onProgress);
    return m.finish();
}
// Same number as measureIntegratedLUFS(); skips true peak (it isn't needed
// and costs ~90% of the meter on dense material).
async function measureIntegratedLUFSAsync(buffer){
    if (!buffer || typeof buffer.getChannelData !== 'function' || buffer.length < 1) return measureIntegratedLUFS(buffer);
    var m = createIntegratedMeter(buffer.sampleRate, buffer.numberOfChannels, { truePeak: false });
    await feedMeterSliced(buffer, m, null);
    return m.finish().integratedLUFS;
}

window.LoudnessR128 = {
    measureIntegratedLUFS: measureIntegratedLUFS,
    measureDetailed: measureDetailed,
    measureDetailedAsync: measureDetailedAsync,
    measureIntegratedLUFSAsync: measureIntegratedLUFSAsync,
    truePeakDbtpEstimate: truePeakDbtpEstimate,
    truePeakDbtpDisplay: truePeakDbtpDisplay,
    applyGainMatch: applyGainMatch,
    matchTo: matchTo,
    createMeter: createMeter,
    createIntegratedMeter: createIntegratedMeter,
    version: '1.3.0'
};

})();
